import { BathProductPage, BathProductConfig } from "@/components/bath/BathProductPage";
import { Rem7HydraSpecPDFButton } from "@/components/bath/Rem7HydraSpecPDFButton";
import { Rem7HydraSpecsPanel, SpecGroup, DimensionMetric, InBoxItem } from "@/components/bath/Rem7HydraSpecsPanel";
import { Rem7HydraFeatureShowcase, FeatureModule } from "@/components/bath/Rem7HydraFeatureShowcase";
import {
  Sparkles, ShieldCheck, Wrench, Layers, TrendingUp, CheckCircle2,
  Home, Building2, Hotel, Waves, Radio,
  Ruler, Droplets, Cog, Lightbulb, Settings2, ShowerHead, Armchair, Package, Square, Anchor,
} from "lucide-react";

/**
 * /:lang/bath-sanitary/rem7-whirlpool-surf
 * REM7 Hydra Surf — 1500×1500mm square corner acrylic whirlpool with
 * 1.5HP surfing water massage, 800mm decorative waterfall and chromotherapy
 * LED bubble bath. The corner-format flagship of the REM7 Hydra programme.
 */
const config: BathProductConfig = {
  slug: "rem7-whirlpool-surf",
  category: "Whirlpool Massage Bathtub",
  productName: "REM7 Hydra Surf Corner Whirlpool Massage Bathtub",
  shortName: "REM7 Hydra Surf",
  sku: "REM7-HYDRA-3153",
  schemaCategory: "Bathtubs > Whirlpool > Corner / Square Acrylic",
  metaTitle: "REM7 Hydra Surf | 1500mm Corner Whirlpool Spa Tub",
  metaDescription:
    "REM7 Hydra Surf — 1500×1500×630mm corner whirlpool with 1.5HP surf massage pump, 800mm waterfall and chromotherapy LED. CE certified.",
  heroEyebrow: "Project Solutions · Corner Whirlpool with Waterfall",
  heroHeadline: (
    <>
      Square Corner Whirlpool with{" "}
      <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-200 bg-clip-text text-transparent">
        Surfing Massage & Waterfall
      </span>
    </>
  ),
  heroIntro:
    "The corner-format flagship of the REM7 Hydra programme. A 1500 × 1500 mm square acrylic whirlpool with a 1.5HP surfing water massage pump, an 800 mm decorative waterfall, chromotherapy LED bubble bath and an 8 mm decorative acrylic skirt with stainless armrest — designed to anchor a signature spa bathroom in branded residences and resort suites.",
  premiumSummary:
    "Built around 100% pure sanitary-grade PMMA acrylic, a 1.5HP whirlpool pump driving 2 large + 8 small ultra-thin water jets, a 300W bubble pump with 8 LED bubble jets, and an 800 mm decorative waterfall with shift switch. Hot/cold mixer, diverter, hand shower, integrated 1-side apron and ON/OFF air switch — a complete corner-format spa bathtub for European luxury projects.",
  galleryImages: [
    {
      src: "/images/bath/whirlpool-surf/hero.jpg",
      alt: "REM7 Hydra Surf 1500mm corner acrylic whirlpool bathtub with waterfall",
      title: "REM7 Hydra Surf — Hero",
      caption: "Hero · Corner whirlpool with waterfall",
      description:
        "Hero of the REM7 Hydra Surf 1500 × 1500 mm corner whirlpool massage bathtub in white acrylic with 800 mm decorative waterfall and chromotherapy LED bubble jets.",
    },
    {
      src: "/images/bath/whirlpool-surf/angle.jpg",
      alt: "REM7 Hydra Surf corner whirlpool three-quarter angle view",
      title: "REM7 Hydra Surf — Angle View",
      caption: "Angle · Square corner format",
      description:
        "Three-quarter angle of the REM7 Hydra Surf corner whirlpool bathtub showing the square 1500 × 1500 mm footprint and 8 mm decorative transparent acrylic apron.",
    },
    {
      src: "/images/bath/whirlpool-surf/controls.jpg",
      alt: "REM7 Hydra Surf air switch control with diverter mixer",
      title: "REM7 Hydra Surf — Control Detail",
      caption: "Detail · ON/OFF air switch",
      description:
        "Macro of the REM7 Hydra Surf control system — ON/OFF air switch, water whirlpool & air whirlpool adjusting switches, and the waterfall / water-massage shift switch.",
    },
    {
      src: "/images/bath/whirlpool-surf/jets.jpg",
      alt: "REM7 Hydra Surf ultra-thin surfing massage jets 2 large 8 small",
      title: "REM7 Hydra Surf — Surfing Jets",
      caption: "Detail · 1.5HP surfing massage",
      description:
        "Detail of the ultra-thin surfing-massage jet array on the REM7 Hydra Surf — 2 large + 8 small water jets driven by a 1.5HP whirlpool pump with run-dry protection.",
    },
    {
      src: "/images/bath/whirlpool-surf/waterfall.jpg",
      alt: "REM7 Hydra Surf 800mm decorative waterfall feature",
      title: "REM7 Hydra Surf — 800mm Waterfall",
      caption: "Feature · 800 mm waterfall",
      description:
        "Detail of the 800 mm decorative waterfall on the REM7 Hydra Surf, with shift switch between waterfall flow and full water-massage mode.",
    },
    {
      src: "/images/bath/whirlpool-surf/detail.jpg",
      alt: "REM7 Hydra Surf 8mm decorative acrylic apron with stainless armrest",
      title: "REM7 Hydra Surf — Apron Detail",
      caption: "Detail · 8 mm acrylic + S.S. armrest",
      description:
        "Close-up of the 8 mm decorative transparent acrylic apron and #304 stainless steel armrest detail on the REM7 Hydra Surf corner whirlpool.",
    },
    {
      src: "/images/bath/whirlpool-surf/lifestyle.jpg",
      alt: "REM7 Hydra Surf corner whirlpool installed in luxury villa bathroom",
      title: "REM7 Hydra Surf — Lifestyle",
      caption: "Lifestyle · Villa spa bathroom",
      description:
        "Lifestyle scene: REM7 Hydra Surf corner whirlpool installed as the centrepiece of a luxury villa spa bathroom with diverter mixer and hand shower.",
    },
  ],
  specs: [
    { label: "Dimensions", value: "1500 × 1500 × 630 mm (L × W × H)" },
    { label: "Format", value: "Square corner whirlpool · single bather" },
    { label: "Shell Material", value: "100% Sanitary-grade PMMA Acrylic" },
    { label: "Reinforcement", value: "Alkali-free fibreglass + resin + wood reinforcement" },
    { label: "Hydro Massage", value: "1.5HP pump · 2 large + 8 small ultra-thin surfing jets" },
    { label: "Air / Bubble Bath", value: "300W bubble pump · 8 LED bubble jets" },
    { label: "Waterfall", value: "800 mm decorative waterfall with shift switch" },
    { label: "Control System", value: "ON/OFF air switch · whirlpool + air adjusting switches" },
    { label: "Pump Protection", value: "Run-without-water protection on hydro pump" },
    { label: "Plumbing", value: "Hot/cold mixer · diverter · hand shower · drainer with overflow" },
    { label: "Apron", value: "Integrated 1-side apron · 8 mm decorative transparent acrylic" },
    { label: "Armrest", value: "#304 stainless steel armrest" },
    { label: "Pillows", value: "2 × dark grey ergonomic headrests" },
    { label: "Support / Feet", value: "Stainless steel frame · pre-levelled height-adjustable feet" },
    { label: "Certification", value: "CE · SGS · cUPC · ISO 9001" },
    { label: "Warranty", value: "5 years shell · 2 years components" },
    { label: "MOQ", value: "From 5 units · Bulk pricing 20+" },
    { label: "Lead Time", value: "20–30 working days · 40ft container ≈ 24 units" },
  ],
  quickFeatures: [
    "1500 × 1500 × 630 mm square corner format",
    "1.5HP hydro pump · 10 ultra-thin surfing jets",
    "800 mm decorative waterfall with shift switch",
    "300W air pump · 8 chromotherapy LED bubble jets",
    "Run-without-water pump protection",
    "8 mm decorative acrylic apron + #304 S.S. armrest",
    "Hot/cold mixer · diverter · hand shower included",
    "100% sanitary-grade PMMA acrylic · CE / SGS / cUPC",
  ],
  productMaterial: "100% Sanitary-grade PMMA Acrylic with fibreglass + resin reinforcement",
  additionalProperties: [
    { name: "Length", value: "1500", unitText: "MMT" },
    { name: "Width", value: "1500", unitText: "MMT" },
    { name: "Height", value: "630", unitText: "MMT" },
    { name: "Hydro Pump Power", value: "1.5", unitText: "HP" },
    { name: "Air Pump Power", value: "300", unitText: "WTT" },
    { name: "Voltage", value: "220V / 50Hz" },
    { name: "Bather Capacity", value: "1" },
    { name: "Hydro Jets", value: "10 (2 large + 8 small)" },
    { name: "LED Bubble Jets", value: "8" },
    { name: "Waterfall", value: "800 mm" },
    { name: "Certification", value: "CE, SGS, cUPC, ISO 9001" },
    { name: "Shell Warranty", value: "5 years" },
    { name: "Component Warranty", value: "2 years" },
  ],
  whyChoose: [
    {
      icon: Waves,
      title: "Surfing massage + waterfall in one tub",
      body: "A 1.5HP whirlpool pump drives a true surfing-style hydro massage, while the 800 mm decorative waterfall doubles as a sensory feature and a refill flow — switchable from a single shift switch.",
    },
    {
      icon: Sparkles,
      title: "Corner-format spa centrepiece",
      body: "The 1500 × 1500 mm square footprint anchors the corner of a primary suite or resort villa bathroom, giving the room a clear hero element that photographs beautifully for short-let listings and brochure shots.",
    },
    {
      icon: TrendingUp,
      title: "Premium ADR for resort & villa rooms",
      body: "Whirlpool suites with a waterfall feature consistently command higher nightly rates and longer minimum stays. The Surf is engineered as a project-grade hero tub for that segment.",
    },
    {
      icon: ShieldCheck,
      title: "Sanitary-grade durability",
      body: "100% pure PMMA acrylic, fibreglass + resin + wood reinforcement, run-dry pump protection, stainless support frame and #304 stainless armrest. CE / SGS / cUPC certified for residential and hospitality.",
    },
    {
      icon: Wrench,
      title: "Specifier-friendly installation",
      body: "Standard EU plumbing rough-in with mixer, diverter and hand shower. Integrated drainer with overflow and pre-levelled adjustable feet keep site work straightforward for the trade.",
    },
    {
      icon: Layers,
      title: "Trade pricing, project margin",
      body: "Hospitality-grade corner whirlpool aesthetics without the European brand premium — structured trade pricing protects developer and contractor margins on phased rollouts.",
    },
    {
      icon: CheckCircle2,
      title: "Coherent REM7 Hydra programme",
      body: "Specify alongside the REM7 Hydra (rectangular 1850 mm) and REM7 Hydra Compact (1800 mm) for a consistent whirlpool language across primary, standard and signature corner suites.",
    },
  ],
  perfectFor: [
    { icon: Hotel, title: "Resort Hotels", body: "Spa suites and villa rooms where the bathtub is the photographable hero." },
    { icon: Home, title: "Branded Residences", body: "Primary suites with a dedicated wet zone for a corner whirlpool centrepiece." },
    { icon: Building2, title: "Private Villas", body: "Owner-occupied villas wanting a true surfing-massage spa experience." },
    { icon: Wrench, title: "Spa Renovations", body: "Bathroom renovations replacing a tired corner tub with a full whirlpool." },
  ],
  faqs: [
    {
      question: "How is the REM7 Hydra Surf different from the rectangular REM7 Hydra?",
      answer:
        "The Surf is the square 1500 × 1500 × 630 mm corner-format variant of the REM7 Hydra programme. It uses the same 1.5HP whirlpool pump and ultra-thin jet philosophy, but adds an 800 mm decorative waterfall and is engineered to sit in a corner of the bathroom rather than along a wall like the standard rectangular tub.",
    },
    {
      question: "What are the dimensions and bathing format?",
      answer:
        "1500 × 1500 × 630 mm (L × W × H), single-bather corner whirlpool. The square footprint is well suited to dedicated wet zones in primary suites, villa bathrooms and resort spa rooms.",
    },
    {
      question: "What does the surfing massage system include?",
      answer:
        "A 1.5HP whirlpool pump drives 2 large + 8 small ultra-thin water jets configured for a surfing-style hydro massage. A separate 300W air bubble pump powers 8 LED chromotherapy bubble jets, controlled independently from the air switch.",
    },
    {
      question: "How does the 800 mm waterfall work?",
      answer:
        "The 800 mm decorative waterfall is plumbed in series with the water-massage circuit and switchable via a single waterfall / water-massage shift switch. It functions both as a sensory feature during use and as a high-flow filling mode.",
    },
    {
      question: "Is the REM7 Hydra Surf CE / cUPC certified for European installation?",
      answer:
        "Yes. The Surf carries CE, SGS, cUPC and ISO 9001 certification, and is engineered around standard EU plumbing rough-in with hot/cold mixer, diverter, hand shower and integrated drainer with overflow.",
    },
    {
      question: "What protections are built into the pump?",
      answer:
        "The 1.5HP whirlpool pump includes run-without-water protection — if the water level drops below the safety threshold, the pump cuts out automatically to prevent dry running and damage to the impeller.",
    },
    {
      question: "What is the warranty on the REM7 Hydra Surf?",
      answer:
        "Aureum VII Trading covers it with a 5-year shell warranty and a 2-year warranty on electrical and mechanical components (pumps, control, LED, mixer). Professional installation by a qualified plumber is required for warranty validity.",
    },
    {
      question: "What is the MOQ and how many units fit a 40ft container?",
      answer:
        "MOQ starts at 5 units, with bulk tiers from 20 units. Production lead time is 20–30 working days. Approximately 24 units of the corner-format Surf fit a 40ft container.",
    },
  ],
  longDescription: [
    "The REM7 Hydra Surf is the corner-format flagship of the REM7 Hydra whirlpool programme — a 1500 × 1500 × 630 mm square acrylic whirlpool engineered to anchor a signature spa bathroom in branded residences, resort hotels and private villas across Europe.",
    "Its defining feature is a 1.5HP whirlpool pump driving a true surfing-style water massage through 2 large and 8 small ultra-thin jets, paired with an 800 mm decorative waterfall that can be switched between sensory waterfall flow and full water-massage mode via a single shift switch.",
    "The bubble bath circuit runs in parallel: a 300W air pump powers 8 colourful LED bubble jets for chromotherapy, controlled independently from the ON/OFF air switch. Two ergonomic dark-grey headrests, a #304 stainless steel armrest and an 8 mm decorative transparent acrylic apron complete the spa-grade aesthetic.",
    "The shell is cast from 100% sanitary-grade PMMA acrylic, reinforced with alkali-free fibreglass, resin and structural wood. A run-without-water protection circuit safeguards the hydro pump, and a stainless support frame with pre-levelled adjustable feet keeps installation level on uneven floors. A complete plumbing package — hot/cold mixer, diverter, hand shower, drainer with overflow — ships with every unit.",
    "Specify the REM7 Hydra Surf in corner spa rooms, alongside the rectangular REM7 Hydra (master suites), the REM7 Hydra Compact (standard rooms) and the ARIA Sculpt / Monolith / Diamond freestanding bathtubs, the VENA basin and vanity range, and the REM7 smart toilet flagship — a coherent Aureum VII bath programme for European luxury projects.",
  ],
};

const dimensionMetrics: DimensionMetric[] = [
  { label: "Length", value: "1500", unit: "mm" },
  { label: "Width", value: "1500", unit: "mm" },
  { label: "Height", value: "630", unit: "mm" },
  { label: "Capacity", value: "1", unit: "Bather" },
];

const specGroups: SpecGroup[] = [
  {
    id: "dimensions",
    icon: Ruler,
    title: "Dimensions & Format",
    rows: [
      { label: "Overall Size", value: "1500 × 1500 × 630 mm" },
      { label: "Format", value: "Square corner · single bather" },
      { label: "Apron", value: "Integrated 1-side · 8 mm decorative acrylic" },
      { label: "Bathing Position", value: "Reclined · 2 headrest pillows" },
      { label: "Net Weight (approx.)", value: "≈ 110 kg" },
    ],
  },
  {
    id: "hydromassage",
    icon: Waves,
    title: "Surfing Hydro Massage",
    rows: [
      { label: "Hydro Pump", value: "1.5 HP whirlpool pump" },
      { label: "Surfing Jets", value: "10 ultra-thin (2 large + 8 small)" },
      { label: "Pump Protection", value: "Run-without-water cut-off circuit" },
      { label: "Jet Adjustment", value: "Whirlpool + air whirlpool switches" },
      { label: "Massage Coverage", value: "Back · lumbar · legs" },
    ],
  },
  {
    id: "bubble",
    icon: Sparkles,
    title: "Air Bubble & LED",
    rows: [
      { label: "Air Pump", value: "300 W bubble pump" },
      { label: "LED Bubble Jets", value: "8 chromotherapy LEDs" },
      { label: "Operating Mode", value: "Independent · ON/OFF air switch" },
      { label: "Lighting", value: "Multi-colour chromotherapy" },
    ],
  },
  {
    id: "waterfeature",
    icon: Droplets,
    title: "Waterfall & Plumbing",
    rows: [
      { label: "Waterfall", value: "800 mm decorative + shift switch" },
      { label: "Mixer", value: "Hot/cold mixer + diverter mixer" },
      { label: "Hand Shower", value: "Included with hose" },
      { label: "Drain", value: "Backwater drainer with overflow" },
      { label: "Armrest", value: "#304 stainless steel armrest" },
    ],
  },
  {
    id: "power",
    icon: Settings2,
    title: "Power & Control",
    rows: [
      { label: "Control", value: "ON/OFF air switch + adjusting switches" },
      { label: "Power Supply", value: "220 V / 50 Hz" },
      { label: "Hydro Pump Power", value: "1.5 HP" },
      { label: "Air Pump Power", value: "300 W" },
    ],
  },
  {
    id: "build",
    icon: ShieldCheck,
    title: "Build, Certification & Logistics",
    rows: [
      { label: "Shell Material", value: "100% sanitary-grade PMMA acrylic" },
      { label: "Reinforcement", value: "Fibreglass + resin + wood" },
      { label: "Support Frame", value: "Stainless steel · adjustable feet" },
      { label: "Certification", value: "CE · SGS · cUPC · ISO 9001" },
      { label: "Warranty", value: "5 yrs shell · 2 yrs components" },
      { label: "MOQ / Container", value: "From 5 units · ≈ 24 / 40ft" },
      { label: "Lead Time", value: "20–30 working days" },
    ],
  },
];

const inBoxItems: InBoxItem[] = [
  { icon: Cog, label: "1.5 HP whirlpool pump", qty: "1 unit · with run-dry guard" },
  { icon: Waves, label: "Ultra-thin surfing jets", qty: "10 jets (2L + 8S)" },
  { icon: Sparkles, label: "300 W air bubble pump", qty: "1 unit" },
  { icon: Lightbulb, label: "Chromotherapy LED bubble jets", qty: "8 LEDs" },
  { icon: Droplets, label: "800 mm decorative waterfall", qty: "1 spout · with shift switch" },
  { icon: Settings2, label: "ON/OFF air + adjusting switches", qty: "1 set" },
  { icon: ShowerHead, label: "Hot/cold mixer + diverter", qty: "1 set + hand shower" },
  { icon: Package, label: "Backwater drainer + overflow", qty: "1 set · pre-fitted" },
  { icon: Armchair, label: "Ergonomic headrest pillows", qty: "2 units · dark grey" },
  { icon: Square, label: "Decorative acrylic apron", qty: "1 × 8 mm panel" },
  { icon: Anchor, label: "#304 stainless steel armrest", qty: "1 unit · pre-installed" },
  { icon: Wrench, label: "Stainless steel support frame", qty: "1 set · adjustable feet" },
];

const featureModules: FeatureModule[] = [
  {
    id: "control",
    icon: Settings2,
    label: "Switch Console",
    tagline: "Module 01 · Control",
    title: "ON/OFF air switch with whirlpool & air adjusting switches",
    description:
      "A clean mechanical control set runs every system on the Surf — ON/OFF air switch, whirlpool and air adjusting switches, and a dedicated waterfall / water-massage shift switch — engineered for reliability in heavy hospitality use.",
    bullets: [
      "ON/OFF air switch · positive tactile feedback",
      "Whirlpool + air whirlpool adjusting switches",
      "Waterfall / water-massage shift switch",
      "No exotic electronics in the wet zone",
    ],
    image: { src: "/images/bath/whirlpool-surf/controls.jpg", alt: "REM7 Hydra Surf air switch control" },
    metric: { value: "1 Switch", label: "Waterfall ↔ Massage" },
  },
  {
    id: "hydro",
    icon: Waves,
    label: "Surfing Massage",
    tagline: "Module 02 · Hydro Massage",
    title: "1.5HP surfing massage with 10 ultra-thin jets",
    description:
      "A 1.5HP whirlpool pump drives a true surfing-style hydro massage through 2 large + 8 small ultra-thin jets, configured for back, lumbar and leg coverage. Run-without-water protection cuts power if the level drops below the safety threshold.",
    bullets: [
      "1.5HP whirlpool pump · run-dry protection circuit",
      "10 ultra-thin water jets (2 large + 8 small)",
      "Adjustable air whirlpool switch for massage intensity",
      "Reclined corner bathing position with 2 headrests",
    ],
    image: { src: "/images/bath/whirlpool-surf/jets.jpg", alt: "REM7 Hydra Surf surfing massage jet array" },
    metric: { value: "10 Jets", label: "1.5HP Surfing Massage" },
  },
  {
    id: "waterfall",
    icon: Droplets,
    label: "800mm Waterfall",
    tagline: "Module 03 · Waterfall",
    title: "800 mm decorative waterfall with shift switch",
    description:
      "The 800 mm decorative waterfall is plumbed in series with the water-massage circuit and switched from a single shift switch — doubling as a sensory feature during use and a high-flow filling mode at the start of the bath.",
    bullets: [
      "800 mm decorative waterfall · sensory feature",
      "Single shift switch · waterfall ↔ full water massage",
      "High-flow filling mode reduces fill time noticeably",
      "Clean satin chrome spout that frames the bath",
    ],
    image: { src: "/images/bath/whirlpool-surf/waterfall.jpg", alt: "REM7 Hydra Surf 800mm decorative waterfall" },
    metric: { value: "800 mm", label: "Decorative Waterfall" },
  },
  {
    id: "led",
    icon: Sparkles,
    label: "LED Bubble",
    tagline: "Module 04 · Bubble Bath",
    title: "300W bubble bath with 8 chromotherapy LED jets",
    description:
      "A separate 300W air pump powers 8 colourful LED bubble jets for chromotherapy, controlled independently from the ON/OFF air switch — a soft bubble bath with mood lighting, without engaging the high-pressure surfing massage.",
    bullets: [
      "300W dedicated air pump · whisper-quiet operation",
      "8 chromotherapy LED bubble jets · floor-mounted",
      "Independent control from the surfing massage circuit",
      "Soft bath / mood lighting mode for evening use",
    ],
    image: { src: "/images/bath/whirlpool-surf/jets.jpg", alt: "REM7 Hydra Surf chromotherapy LED bubble jets" },
    metric: { value: "8 LEDs", label: "Chromotherapy Bath" },
  },
  {
    id: "apron",
    icon: Square,
    label: "Acrylic Apron",
    tagline: "Module 05 · Aesthetic",
    title: "8 mm decorative acrylic apron with #304 stainless armrest",
    description:
      "An 8 mm decorative transparent acrylic apron carries a #304 stainless steel armrest with anti-corrosion finish — engineered to anchor the corner of a primary suite or villa spa room and photograph beautifully.",
    bullets: [
      "8 mm decorative transparent acrylic apron",
      "#304 stainless steel armrest · anti-corrosion finish",
      "Integrated 1-side apron for corner installation",
      "Service access for pumps and electronics",
    ],
    image: { src: "/images/bath/whirlpool-surf/detail.jpg", alt: "REM7 Hydra Surf decorative acrylic apron with armrest" },
    metric: { value: "8 mm", label: "Decorative Acrylic" },
  },
  {
    id: "build",
    icon: ShieldCheck,
    label: "Build Quality",
    tagline: "Module 06 · Materials",
    title: "Sanitary-grade PMMA acrylic on stainless frame",
    description:
      "100% pure sanitary-grade PMMA acrylic shell — high-gloss, scratch / stain / fade resistant — reinforced with alkali-free fibreglass, resin and structural wood, mounted on a stainless steel frame with pre-levelled feet and CE / SGS / cUPC certification.",
    bullets: [
      "100% sanitary-grade PMMA acrylic shell",
      "Fibreglass + resin + wood reinforcement layers",
      "Stainless steel structural support frame",
      "CE · SGS · cUPC · ISO 9001 certified",
    ],
    image: { src: "/images/bath/whirlpool-surf/lifestyle.jpg", alt: "REM7 Hydra Surf installed in luxury villa bathroom" },
    metric: { value: "5-Year", label: "Shell Warranty" },
  },
];

const Rem7WhirlpoolSurf = () => (
  <BathProductPage
    config={{
      ...config,
      specsPdfButton: (
        <Rem7HydraSpecPDFButton specs={config.specs} features={config.quickFeatures ?? []} />
      ),
      featureShowcase: (
        <Rem7HydraFeatureShowcase shortName={config.shortName} modules={featureModules} />
      ),
      specsPanel: (
        <Rem7HydraSpecsPanel
          shortName={config.shortName}
          productName={config.productName}
          sku={config.sku}
          heroImage={{
            src: "/images/bath/whirlpool-surf/angle.jpg",
            alt: "REM7 Hydra Surf corner whirlpool dimensional reference view",
          }}
          dimensions={dimensionMetrics}
          groups={specGroups}
          inBox={inBoxItems}
          certifications={["CE", "SGS", "cUPC", "ISO 9001"]}
          pdfButton={
            <Rem7HydraSpecPDFButton specs={config.specs} features={config.quickFeatures ?? []} />
          }
        />
      ),
    }}
  />
);
export default Rem7WhirlpoolSurf;
